import "../../components/ProfileCard/ProfileCard.scss";
import {FormEvent, useEffect, useState} from "react";
import axios from "axios";
import {diffDays} from "../../../../shared/functions/diffDays";
import {getCurrentDateInSeconds} from "../../../../shared/functions/getRandomId";
import Toaster, {ToasterProps} from "../../../../shared/Toaster/Toaster";
import {PATHS_API} from "../../../../../enum";
import {CardHead, SpinButton, withMinSpin} from "../../components/ProfileCard/ProfileCard";

interface ICreator {
    codeCreator: string
    percentForReferral: number
    percentForCreator?: number
    cardWithdraw?: string
    amountWithdraw?: number
    dateStart?: string
    dateEnd?: string
}

const REFERRAL_DAYS = 30;

const HandshakeIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 17l2 2a1 1 0 003-3" />
        <path d="M14 14l2.5 2.5a1 1 0 003-3l-3.9-3.9a3 3 0 00-4.2 0l-.9.9a1 1 0 11-3-3L10.3 4a5 5 0 016.4-.6L18 4.3 21 3v10l-1 1" />
        <path d="M3 3l-1 1v10l6.5 6.5a1 1 0 003-3" />
        <path d="M3 4h8" />
    </svg>
);

// маскуємо номер картки: видно лише останні 4 цифри
const maskCard = (card?: string) => card ? `•••• •••• •••• ${card.replace(/\s/g, "").slice(-4)}` : "";

// Реферальна програма: три стани — партнер (автор контенту), реферал, поки не бере участі
const Referral = () => {
    const [data, setData] = useState<ICreator | null>(null)
    const [loaded, setLoaded] = useState(false)
    const [code, setCode] = useState("")
    const [loading, setLoading] = useState(false)
    const [toastProps, setToastProps] = useState<ToasterProps | null>(null);

    const load = () => axios.get(`${PATHS_API.REFERRAL}/creator`, { withCredentials: true })
        .then(({ data }) => setData(data || null))
        .catch(() => setData(null))
        .finally(() => setLoaded(true))

    useEffect(() => { load() }, [])

    const apply = async (e: FormEvent) => {
        e.preventDefault()
        const value = code.trim()
        if (!value || loading) return
        setLoading(true)
        let toast: ToasterProps
        await withMinSpin(async () => {
            try {
                await axios.post(PATHS_API.REFERRAL, { codeCreator: value }, { withCredentials: true })
                toast = { message: "Код застосовано", type: "success", id: getCurrentDateInSeconds() }
            } catch (error: any) {
                // успіх сервер теж повертає кодом 201 через виняток — тоді в тілі є повідомлення
                const status = error.response?.status
                const message = error.response?.data?.message
                toast = status === 201
                    ? { message: message || "Код застосовано", type: "success", id: getCurrentDateInSeconds() }
                    : { message: typeof message === "string" ? message : "Не вдалося застосувати код", type: "error", id: getCurrentDateInSeconds() }
            }
        })
        setLoading(false)
        setToastProps(toast!)
        setCode("")
        load()
    }

    if (!loaded) return null

    const isCreator = !!data?.percentForCreator
    const isReferral = !isCreator && !!data?.codeCreator
    const daysLeft = isReferral && data?.dateEnd ? Math.max(0, diffDays(data.dateEnd)) : 0

    return (
        <div className="bc-page">
            { isCreator &&
                <div className="bc-card">
                    <CardHead
                        icon={<HandshakeIcon />}
                        title="Ви партнер проєкту"
                        text={<>Дякуємо! Ваші реферали вводять код <span className="bc-chip">{data!.codeCreator}</span>, а {data!.percentForCreator}% їхніх поповнень надходять на ваш баланс для виплати.</>}
                    />
                    <div className="bc-stats">
                        <div className="bc-stat bc-stat-accent"><span>Баланс до виплати</span><b>{data!.amountWithdraw ?? 0} ₴</b></div>
                        <div className="bc-stat"><span>Ваш відсоток</span><b>{data!.percentForCreator}%</b></div>
                        <div className="bc-stat"><span>Бонус рефералам</span><b>+{data!.percentForReferral}%</b></div>
                    </div>
                </div>
            }

            { isCreator &&
                <div className="bc-hint">
                    <div>
                        <b>Картка для виплат</b>
                        <span>{ data!.cardWithdraw ? maskCard(data!.cardWithdraw) : "Картку ще не вказано." } Щоб додати чи змінити картку, напишіть адміністрації в Discord.</span>
                    </div>
                </div>
            }

            { isReferral &&
                <div className="bc-card">
                    <CardHead
                        icon={<HandshakeIcon />}
                        title={<>Ви реферал <span className="bc-chip">{data!.codeCreator}</span></>}
                        text={`Бонус діє ще ${daysLeft} дн. Після цього код автора потрібно ввести повторно.`}
                    />
                    <div className="bc-stats">
                        <div className="bc-stat bc-stat-accent"><span>Бонус до поповнення</span><b>+{data!.percentForReferral}%</b></div>
                        <div className="bc-stat"><span>Залишилось</span><b>{daysLeft} дн.</b></div>
                    </div>
                    <div className="bc-progress" aria-hidden="true">
                        <i style={{ width: `${Math.min(100, (daysLeft / REFERRAL_DAYS) * 100)}%` }} />
                    </div>
                </div>
            }

            { !isCreator && !isReferral &&
                <form className="bc-card" onSubmit={apply}>
                    <CardHead
                        icon={<HandshakeIcon />}
                        title="Реферальна програма"
                        text="Введіть код автора контенту — і отримуйте бонус до кожного поповнення балансу протягом 30 днів."
                    />
                    <div className="bc-field">
                        <input
                            className="bc-input"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="Код автора"
                            autoComplete="off"
                            spellCheck={false}
                            maxLength={64}
                            aria-label="Код автора"
                        />
                        <SpinButton loading={loading} disabled={!code.trim()}>Застосувати</SpinButton>
                    </div>
                </form>
            }

            { !isCreator &&
                <div className="bc-hint">
                    <div>
                        <b>Хочете стати партнером?</b>
                        <span>Якщо ви автор контенту про ASKOLD — напишіть нам у Discord, і ми створимо ваш особистий код.</span>
                    </div>
                    <a className="bc-discord" href="https://discord.gg/GC44JMuAGQ" target="_blank" rel="noopener noreferrer">Discord</a>
                </div>
            }

            {toastProps && <Toaster message={toastProps.message} type={toastProps.type} id={toastProps.id} />}
        </div>
    );
};

export default Referral;
